import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useHistory } from "react-router-dom";
import logger from "../../utils/logger";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import MenuItem from "@material-ui/core/MenuItem";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";
import { listWhatsapps } from "../../services/whatsapp.api";
import { updateTicket, getTicket } from "../../services/tickets.api";
import { listUsers } from "../../services/users.api";
import { findAllQueues, checkQueueAI as checkQueueAIApi } from "../../services/queue.api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import toastSuccess from "../../errors/toastSuccess";
// ✅ REMOVIDO: useQueues ya no se usa para evitar bucle infinito
import { AuthContext } from "../../context/Auth/AuthContext";
import TransferConfirmationModal from "../TransferConfirmationModal";
import ConnectionWarningModal from "../ConnectionWarningModal";

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid }) => {
  logger.transferModal.debug("🎯 TransferTicketModalCustom renderizado - modalOpen:", modalOpen);
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [autocompleteOpen, setAutocompleteOpen] = useState(false);
  const isMounted = useRef(true);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;

  // ✅ NUEVO: Estados para modal de confirmación
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [queueAIInfo, setQueueAIInfo] = useState(null);
  const [transferring, setTransferring] = useState(false);
  
  // ✅ NUEVO: Estados para modal de advertencia de conexión desconectada
  const [connectionWarningOpen, setConnectionWarningOpen] = useState(false);
  const [connectionWarningData, setConnectionWarningData] = useState(null);

  // ✅ NUEVO: Función para verificar IA del departamento
  const checkQueueAI = useCallback(async (queueId) => {
    if (!queueId) return null;
    
    try {
      logger.transferModal.debug("🔍 Verificando IA del departamento:", queueId);
      const { data } = await checkQueueAIApi(queueId);
      
      if (data.success) {
        logger.transferModal.debug("✅ Información IA obtenida:", data.data);
        return data.data;
      } else {
        logger.transferModal.error("❌ Error verificando IA:", data.error);
        return null;
      }
    } catch (error) {
      logger.transferModal.error("❌ Error verificando IA del departamento:", error);
      return null;
    }
  }, []);

  // ✅ CORREGIDO: Función de búsqueda optimizada con useCallback - MOVIDA ANTES DEL useEffect
  // ✅ NUEVO: Agregar parámetro queueId para filtrar usuarios por departamento
  const fetchUsers = useCallback(async (searchTerm, queueId = null) => {
    if (!isMounted.current) return;
    
    try {
      logger.transferModal.debug("🔍 Buscando usuarios...", { searchTerm, queueId });
      
      const params = { 
        searchParam: searchTerm || "",
        limit: 10
      };
      
      // ✅ NUEVO: Si hay un departamento seleccionado, filtrar usuarios por ese departamento
      if (queueId) {
        params.queueIds = JSON.stringify([queueId]);
        logger.transferModal.debug("🔍 Filtrando usuarios por departamento:", queueId);
      }
      
      const { data } = await listUsers(params);
      
      if (isMounted.current) {
        logger.transferModal.debug("✅ Usuarios encontrados:", data.users);
        setOptions(data.users);
      }
    } catch (err) {
      if (isMounted.current) {
        logger.transferModal.error("❌ Error al buscar usuarios:", err);
        toastError(err);
      }
    }
  }, []); // ✅ Dependencias vacías ya que solo usa variables del scope

  // ✅ CORREGIDO: Cleanup al desmontar
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ✅ CORREGIDO: useEffect optimizado SIN findAllQueues en dependencias para evitar bucle
  useEffect(() => {
    
    if (!modalOpen) {
      return;
    }

    // console.log("✅ Modal abierto, inicializando...");

    const initializeModal = async () => {
      if (!isMounted.current) return;
      
      // console.log("🚀 Iniciando carga de datos del modal...");
      setLoading(true);
      try {
        // Cargar whatsapps
        const { data } = await listWhatsapps({ companyId, session: 0 });
        if (isMounted.current) {
          setWhatsapps(data);
        }

        /**
         * La conexión por defecto es la DEL TICKET, no la del operador.
         *
         * 🔴 DT-17: venía de `user.whatsappId`, así que quien tuviera otro
         * número por defecto transfería y **movía la conversación a su número
         * sin pretenderlo** — el cliente pasaba a recibir mensajes desde otro
         * teléfono. El backend tiene maquinaria dedicada a ese cambio
         * (`oldWhatsappId`, `isChangingWhatsapp`): no es inocuo.
         *
         * Con el origen corregido, el campo obligatorio se satisface siempre
         * solo —no hay tickets sin conexión— y cambiarla pasa a ser un acto
         * deliberado en vez de un efecto colateral (doc 0391 §11.9.7).
         */
        let conexionDelTicket = null;
        try {
          const { data: t } = await getTicket(ticketid);
          conexionDelTicket = t?.whatsappId ?? null;
        } catch (err) {
          // Sin el ticket se cae al valor del operador, que es lo que había
          // antes: preferible a dejar el campo obligatorio vacío.
          conexionDelTicket = null;
        }

        // Configurar valores por defecto
        if (isMounted.current) {
          const porDefecto = conexionDelTicket ?? whatsappId;
          if (porDefecto !== null && porDefecto !== undefined) {
            setSelectedWhatsapp(porDefecto);
          }
          if (user.queues.length === 1) {
            setSelectedQueue(user.queues[0].id);
          }
        }

        // ✅ CORREGIDO: Cargar colas directamente sin usar hook para evitar bucle
        // ✅ CORRECCIÓN 2025-12-23: Cargar TODOS los departamentos, no solo los que tienen IA
        // Esto permite transferir tickets a cualquier departamento, incluso sin IA configurada
        let queuesData = [];
        try {
          // ✅ Cargar TODOS los departamentos de la empresa
          const response = await findAllQueues();
          queuesData = response.data;
          logger.transferModal.debug("✅ Departamentos cargados:", queuesData);
        } catch (error) {
          logger.transferModal.error("❌ Error cargando departamentos:", error);
          queuesData = [];
        }
        
        if (isMounted.current) {
          setAllQueues(queuesData);
          setQueues(queuesData);
          
          // ✅ VALIDACIÓN: Asegurar que selectedQueue esté en las opciones disponibles
          if (selectedQueue && !queuesData.find(q => q.id === selectedQueue)) {
            logger.transferModal.debug("⚠️ Valor selectedQueue no válido, reseteando:", selectedQueue);
            setSelectedQueue("");
          }
        }

        // ✅ Cargar usuarios iniciales (sin filtro de departamento)
        await fetchUsers("", null);
        
      } catch (error) {
        if (isMounted.current) {
          // console.error("❌ Error inicializando modal:", error);
          logger.transferModal.error("❌ Error inicializando modal:", error);
        }
      } finally {
        if (isMounted.current) {
          // console.log("🏁 Finalizando carga de datos del modal");
          setLoading(false);
        }
      }
    };

    initializeModal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen, companyId, user.queues, whatsappId]); // ✅ REMOVIDO findAllQueues para evitar bucle

  // ✅ CORREGIDO: Búsqueda con debounce optimizada
  // ✅ NUEVO: Incluir selectedQueue para filtrar usuarios cuando cambia el departamento
  useEffect(() => {
    if (!modalOpen || !searchParam || searchParam.length < 2) return;

    const delayDebounceFn = setTimeout(() => {
      if (isMounted.current) {
        // ✅ Si hay un departamento seleccionado, filtrar usuarios por ese departamento
        fetchUsers(searchParam, selectedQueue || null);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen, fetchUsers, selectedQueue]); // ✅ Incluir selectedQueue

  // ✅ ELIMINADO: useEffect duplicado que causaba bucle infinito
  // Los usuarios se cargan en el useEffect principal de initializeModal

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setSelectedUser(null);
    setAutocompleteOpen(false);
    setOptions([]);
  };

  // ✅ NUEVO: Función para manejar la transferencia con confirmación
  const handleSaveTicket = async (e) => {
    e.preventDefault();
    if (!ticketid) return;

    /**
     * REGLA DEL DESTINO (doc 0391 §11.9.3): departamento, persona, o ambos.
     * Nunca ninguno.
     *
     * 🔴 DT-18: antes era `if (!selectedQueue) return;` — un `return` MUDO. Se
     * pulsaba «Transferir» y no ocurría nada, sin mensaje. Y además exigía
     * departamento siempre, lo que impedía pasarle un ticket a un compañero en
     * un negocio que no usa departamentos.
     *
     * La regla de verdad vive en `UpdateTicketService`; esto solo evita el
     * viaje al servidor y explica el porqué.
     */
    if (!selectedQueue && !selectedUser) {
      toast.error(i18n.t("transferTicketModal.sinDestino"));
      return;
    }

    // Sin departamento no hay nada que preguntarle a la IA sobre él: la entrega
    // a una persona va directa.
    if (!selectedQueue) {
      await executeTransfer();
      return;
    }
    
    if (!isMounted.current) return;
    
    logger.transferModal.debug("🎯 Iniciando proceso de transferencia para queueId:", selectedQueue);
    
    // ✅ PASO 1: Verificar si el departamento tiene IA
    const aiInfo = await checkQueueAI(selectedQueue);
    
    // ✅ DEBUG: Logging detallado
    logger.transferModal.debug("🔍 AI Info recibida:", aiInfo);
    logger.transferModal.debug("🔍 Queues disponibles:", queues.map(q => ({ id: q.id, name: q.name })));
    logger.transferModal.debug("🔍 Selected Queue ID:", selectedQueue);
    
    // ✅ CORREGIDO: Preparar información completa antes de abrir modal
    let finalQueueInfo;
    
    if (aiInfo && aiInfo.hasAI === true) {
      // ✅ DEPARTAMENTO CON AUTOMATIZACIÓN (FLOWBUILDER, PROMPT, INTEGRACIÓN o CHATBOT)
      logger.transferModal.debug("🤖 Departamento con automatización detectado:", aiInfo);
      finalQueueInfo = {
        queueName: aiInfo.queueName,
        hasAI: true,
        automationType: aiInfo.automationType, // ✅ "flowbuilder" | "prompt" | "integration" | "chatbot"
        automationName: aiInfo.automationName, // ✅ Nombre del FlowBuilder/prompt/integración/chatbot
        promptId: aiInfo.promptId,
        promptName: aiInfo.promptName,
        promptProvider: aiInfo.promptProvider,
        integrationId: aiInfo.integrationId,
        flowBuilderId: aiInfo.flowBuilderId,
        flowBuilderName: aiInfo.flowBuilderName,
        hasChatbot: aiInfo.hasChatbot,
        chatbotOptionsCount: aiInfo.chatbotOptionsCount
      };
    } else {
      // ✅ DEPARTAMENTO SIN AUTOMATIZACIÓN
      logger.transferModal.debug("👤 Departamento sin automatización detectado");
      logger.transferModal.debug("🔍 aiInfo completo:", aiInfo);
      logger.transferModal.debug("🔍 aiInfo?.queueName:", aiInfo?.queueName);
      
      // ✅ CORREGIDO: Usar queueName del backend en lugar de buscar en queues
      const queueName = aiInfo?.queueName || queues.find(q => q.id === selectedQueue)?.name || "Departamento";
      logger.transferModal.debug("📋 Nombre del departamento encontrado:", queueName);
      
      finalQueueInfo = {
        queueName: queueName,
        hasAI: false,
        automationType: null,
        automationName: null,
        promptId: null,
        promptName: null
      };
    }
    
    // ✅ CORREGIDO: Solo abrir modal cuando tengamos toda la información
    logger.transferModal.debug("📋 QueueAIInfo final que se va a setear:", finalQueueInfo);
    logger.transferModal.debug("📋 queueName que se va a pasar al modal:", finalQueueInfo.queueName);
    
    // ✅ VALIDACIÓN: Verificar estado de conexión WhatsApp ANTES de abrir modal de confirmación
    if (selectedWhatsapp) {
      const selectedWhatsappObj = whatsapps.find(w => w.id === selectedWhatsapp);
      if (selectedWhatsappObj && selectedWhatsappObj.status !== 'CONNECTED') {
        const whatsappName = selectedWhatsappObj.name || `Conexión ${selectedWhatsappObj.id}`;
        
        // ✅ MOSTRAR MODAL DE ADVERTENCIA en lugar de toast rápido
        setConnectionWarningData({
          connectionName: whatsappName,
          connectionStatus: selectedWhatsappObj.status
        });
        setConnectionWarningOpen(true);
        return;
      }
    }

    // ✅ SOLUCIÓN DEFINITIVA: Solo abrir modal si tenemos queueName válido
    if (finalQueueInfo.queueName && finalQueueInfo.queueName !== "Departamento") {
      setQueueAIInfo(finalQueueInfo);
      setConfirmationModalOpen(true);
    } else {
      logger.transferModal.error("❌ No se puede abrir modal: queueName no válido:", finalQueueInfo.queueName);
      toastError("Error: No se pudo obtener el nombre del departamento");
    }
  };

  // ✅ NUEVO: Función para ejecutar la transferencia después de confirmar
  const executeTransfer = async () => {
    if (!isMounted.current) return;
    
    // ✅ VALIDACIÓN: Verificar estado de conexión WhatsApp antes de transferir
    if (selectedWhatsapp) {
      const selectedWhatsappObj = whatsapps.find(w => w.id === selectedWhatsapp);
      if (selectedWhatsappObj && selectedWhatsappObj.status !== 'CONNECTED') {
        const whatsappName = selectedWhatsappObj.name || `Conexión ${selectedWhatsappObj.id}`;
        
        // ✅ MOSTRAR MODAL DE ADVERTENCIA en lugar de toast rápido
        setConnectionWarningData({
          connectionName: whatsappName,
          connectionStatus: selectedWhatsappObj.status
        });
        setConnectionWarningOpen(true);
        setTransferring(false);
        return;
      }
    }
    
    setTransferring(true);
    try {
      let data = {};

      if (selectedUser) {
        data.userId = selectedUser.id;
      }

      if (selectedQueue && selectedQueue !== null) {
        data.queueId = selectedQueue;

        if (!selectedUser) {
          data.status = "pending";
          data.userId = null;
        }
      }

      if (selectedWhatsapp) {
        data.whatsappId = selectedWhatsapp;
      }
      
      logger.transferModal.debug("🚀 Ejecutando transferencia con datos:", data);
      await updateTicket(ticketid, data);

      if (isMounted.current) {
        // ✅ MOSTRAR MENSAJE DE ÉXITO
        const queueName = queueAIInfo?.queueName || queues.find(q => q.id === selectedQueue)?.name || "Departamento";
        toastSuccess(i18n.t("transferConfirmationModal.successMessage", { queueName }));
        
        // ✅ ACTUALIZAR LISTA DE TICKETS AUTOMÁTICAMENTE
        // Disparar evento personalizado para refrescar tickets
        window.dispatchEvent(new CustomEvent('ticketsRefresh', {
          detail: { 
            reason: 'transfer_completed',
            ticketId: ticketid,
            newQueueId: selectedQueue
          }
        }));
        
        // ✅ CERRAR MODALES Y REDIRIGIR
        setConfirmationModalOpen(false);
        handleClose();
        history.push(`/tickets`);
      }
    } catch (err) {
      if (isMounted.current) {
        logger.transferModal.error("❌ Error ejecutando transferencia:", err);
        
        // ✅ MEJORAR FEEDBACK: Mensajes de error más claros y específicos
        let errorMessage = "Error al transferir el ticket";
        
        if (err.response?.data?.error === "ERR_WAPP_NOT_INITIALIZED") {
          errorMessage = "⚠️ La conexión WhatsApp seleccionada no está disponible. Por favor, verifique que la conexión esté conectada antes de transferir.";
        } else if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.message) {
          errorMessage = err.message;
        }
        
        toastError(errorMessage);
      }
    } finally {
      if (isMounted.current) {
        setTransferring(false);
      }
    }
  };

  // ✅ NUEVO: Función para cancelar confirmación
  const handleCancelConfirmation = () => {
    setConfirmationModalOpen(false);
    setQueueAIInfo(null);
    setTransferring(false);
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="sm" scroll="paper">
      <form onSubmit={handleSaveTicket}>
        <DialogTitle id="form-dialog-title">
          {i18n.t("transferTicketModal.title")}
        </DialogTitle>
        <DialogContent dividers style={{ width: 400, padding: '20px' }}>
          <Autocomplete
            style={{ width: '100%', marginBottom: 20 }}
            getOptionLabel={(option) => {
              // ✅ VALIDACIÓN: Manejar opciones nulas o vacías
              if (!option || !option.name) return "";
              return option.name;
            }}
            value={selectedUser}
            onChange={(e, newValue) => {
              if (!isMounted.current) return;
              
              // ✅ VALIDACIÓN: Solo asignar si el valor es válido
              if (newValue && newValue.name && newValue.name.trim() !== "") {
                logger.transferModal.debug("👤 Usuario seleccionado:", newValue);
                setSelectedUser(newValue);
                
                // ✅ NUEVO: Si el usuario tiene departamentos, filtrar la lista de departamentos
                if (newValue != null && Array.isArray(newValue.queues) && newValue.queues.length > 0) {
                  logger.transferModal.debug("🔍 Filtrando departamentos por usuario:", newValue.queues);
                  setQueues(newValue.queues);
                  
                  // ✅ Si el usuario tiene solo un departamento, preseleccionarlo
                  if (newValue.queues.length === 1) {
                    setSelectedQueue(newValue.queues[0].id);
                  } else if (selectedQueue && !newValue.queues.find(q => q.id === selectedQueue)) {
                    // ✅ Si el departamento seleccionado no está en los del usuario, limpiar selección
                    setSelectedQueue("");
                  }
                } else {
                  // ✅ Si el usuario no tiene departamentos, mostrar todos los departamentos
                  setQueues(allQueues);
                  setSelectedQueue("");
                }
              } else {
                setSelectedUser(null);
                // ✅ Al limpiar usuario, mostrar todos los departamentos nuevamente
                setQueues(allQueues);
                setSelectedQueue("");
              }
            }}
            options={options}
            getOptionSelected={(option, value) => {
              // ✅ VALIDACIÓN: Comparación segura de opciones
              if (!option || !value) return false;
              return option.id === value.id;
            }}
            filterOptions={(options, { inputValue }) => {
              logger.transferModal.debug("🔍 Filtrando opciones, inputValue:", inputValue);
              if (!inputValue || inputValue.trim() === "") {
                return options;
              }
              return options.filter(option => {
                // ✅ VALIDACIÓN: Solo procesar opciones válidas
                if (!option || !option.name || !option.email) return false;
                const searchTerm = inputValue.toLowerCase();
                return (
                  option.name.toLowerCase().includes(searchTerm) ||
                  option.email.toLowerCase().includes(searchTerm)
                );
              });
            }}
            freeSolo={false}
            autoHighlight
            noOptionsText={i18n.t("transferTicketModal.noOptions")}
            loading={loading}
            open={autocompleteOpen}
            onOpen={() => {
              if (!isMounted.current) return;
              
              logger.transferModal.debug("📱 Autocomplete abierto, options.length:", options.length);
              setAutocompleteOpen(true);
              if (options.length === 0) {
                logger.transferModal.debug("🔄 Cargando usuarios desde onOpen...");
                setSearchParam("");
                // ✅ NUEVO: Si hay un departamento seleccionado, cargar usuarios filtrados
                fetchUsers("", selectedQueue || null);
              }
            }}
            onClose={() => {
              if (!isMounted.current) return;
              
              logger.transferModal.debug("📱 Autocomplete cerrado");
              setAutocompleteOpen(false);
            }}
            ListboxProps={{
              style: { maxHeight: 200 },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t("transferTicketModal.fieldLabel")}
                variant="outlined"
                autoFocus
                onChange={(e) => {
                  if (!isMounted.current) return;
                  
                  const value = e.target.value;
                  logger.transferModal.debug("✏️ Input cambiado:", value);
                  setSearchParam(value);
                  if (!value || value.trim() === "") {
                    logger.transferModal.debug("🔄 Campo vacío, actualizando lista...");
                  }
                }}
                placeholder="Click aquí para ver usuarios disponibles"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={(option) => (
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column',
                padding: '8px 0'
              }}>
                <div style={{ fontWeight: 'bold' }}>
                  {option.name}
                </div>
                {option.email && (
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666',
                    marginTop: '2px'
                  }}>
                    {option.email}
                  </div>
                )}
              </div>
            )}
          />
          <FormControl variant="outlined" style={{ width: '100%', marginTop: 20, marginBottom: 20 }}>
            <Select
              required
              fullWidth
              displayEmpty
              variant="outlined"
              value={selectedQueue || ""}
              onChange={(e) => {
                if (!isMounted.current) return;
                const newQueueId = e.target.value;
                logger.transferModal.debug("🏢 Departamento seleccionado:", newQueueId);
                setSelectedQueue(newQueueId);
                
                // ✅ NUEVO: Cuando se selecciona un departamento, filtrar usuarios por ese departamento
                if (newQueueId) {
                  logger.transferModal.debug("🔍 Filtrando usuarios por departamento:", newQueueId);
                  fetchUsers(searchParam || "", newQueueId);
                } else {
                  // ✅ Si se limpia el departamento, mostrar todos los usuarios
                  fetchUsers(searchParam || "", null);
                }
              }}
              MenuProps={{
                anchorOrigin: {
                  vertical: "bottom",
                  horizontal: "left",
                },
                transformOrigin: {
                  vertical: "top",
                  horizontal: "left",
                },
                getContentAnchorEl: null,
              }}
              renderValue={() => {
                if (selectedQueue === "") {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                      <span style={{ marginRight: '8px' }} role="img" aria-label="Departamento">🏢</span>
                      <span>{i18n.t("transferTicketModal.fieldQueuePlaceholder") || "Seleccione un departamento"}</span>
                    </div>
                  )
                }
                const queue = queues.find(q => q.id === selectedQueue);
                if (!queue) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                      <span style={{ marginRight: '8px' }} role="img" aria-label="Departamento">🏢</span>
                      <span>{i18n.t("transferTicketModal.fieldQueuePlaceholder") || "Seleccione un departamento"}</span>
                    </div>
                  );
                }
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }} role="img" aria-label="Departamento">🏢</span>
                    <span>{queue.name}</span>
                  </div>
                )
              }}
            >
              {queues.map((queue) => {
                // ✅ Determinar si tiene automatización (incluyendo FlowBuilder)
                const hasPrompt = queue.promptId !== null && queue.promptId !== undefined;
                const hasIntegration = queue.integrationId !== null && queue.integrationId !== undefined;
                const hasChatbot = queue.options && Array.isArray(queue.options) && queue.options.length > 0;
                const hasFlowBuilder = queue.flowBuilderId !== null && queue.flowBuilderId !== undefined;
                const hasAutomation = hasPrompt || hasIntegration || hasChatbot || hasFlowBuilder;
                const automationText = hasAutomation ? "Con automatización" : "Sin automatización";
                
                return (
                  <MenuItem dense key={queue.id} value={queue.id}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="Departamento">🏢</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                  {queue.name}
                        </span>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {automationText}
                        </span>
                      </div>
                    </div>
                </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          
          <FormControl variant="outlined" style={{ width: '100%', marginBottom: 20 }}>
              <Select
                fullWidth
                displayEmpty
                variant="outlined"
              value={selectedWhatsapp || ""}
                onChange={(e) => {
                  if (!isMounted.current) return;
                  setSelectedWhatsapp(e.target.value);
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === "") {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                      <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                      <span>{i18n.t("transferTicketModal.fieldConnectionPlaceholder") || "Seleccione una conexión"}</span>
                    </div>
                  )
                  }
                  const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp)
                if (!whatsapp) {
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', color: '#999' }}>
                      <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                      <span>{i18n.t("transferTicketModal.fieldConnectionPlaceholder") || "Seleccione una conexión"}</span>
                    </div>
                  )
                }
                return (
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '8px' }} role="img" aria-label="WhatsApp">📱</span>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                        {whatsapp.name || `Conexión ${whatsapp.id}`}
                      </span>
                      <span style={{ fontSize: '12px', color: '#666' }}>
                        {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                         whatsapp.name ? whatsapp.name : 
                         `Conexión ${whatsapp.id}`}
                      </span>
                    </div>
                  </div>
                )
                }}
              >
                {whatsapps?.length > 0 &&
                whatsapps.map((whatsapp) => (
                  <MenuItem dense key={whatsapp.id} value={whatsapp.id}>
                    <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <span style={{ marginRight: '8px', fontSize: '16px' }} role="img" aria-label="WhatsApp">📱</span>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                            {whatsapp.name || `Conexión ${whatsapp.id}`}
                          </span>
                          <span 
                            style={{ 
                              fontSize: '10px', 
                              padding: '2px 6px', 
                              borderRadius: '10px',
                              backgroundColor: whatsapp.status === 'CONNECTED' ? '#25d366' : '#e1306c',
                              color: 'white',
                              fontWeight: 'bold'
                            }}
                          >
                            {whatsapp.status}
                          </span>
                        </div>
                        <span style={{ fontSize: '12px', color: '#666' }}>
                          {whatsapp.waName ? `${whatsapp.name} - ${whatsapp.waName}` : 
                           whatsapp.name ? whatsapp.name : 
                           `Conexión ${whatsapp.id}`}
                        </span>
                      </div>
                    </div>
                    </MenuItem>
                  ))}
              </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={loading}
            variant="outlined"
          >
            {i18n.t("transferTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="submit"
            color="primary"
            loading={loading}
          >
            {i18n.t("transferTicketModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
      </form>

      {/* ✅ NUEVO: Modal de confirmación separado */}
      <TransferConfirmationModal
        open={confirmationModalOpen}
        onClose={handleCancelConfirmation}
        onConfirm={executeTransfer}
        queueName={queueAIInfo?.queueName}
        hasAI={queueAIInfo?.hasAI}
        automationType={queueAIInfo?.automationType}
        automationName={queueAIInfo?.automationName}
        promptName={queueAIInfo?.promptName}
        loading={transferring}
      />

      {/* ✅ NUEVO: Modal de advertencia de conexión desconectada */}
      <ConnectionWarningModal
        open={connectionWarningOpen}
        onClose={() => {
          setConnectionWarningOpen(false);
          setConnectionWarningData(null);
        }}
        connectionName={connectionWarningData?.connectionName || ""}
        connectionStatus={connectionWarningData?.connectionStatus || ""}
      />
    </Dialog>
  );
};

export default TransferTicketModalCustom;
